import { AWARDS, awardHref, CERTIFICATE_TEMPLATES } from "../../../routes";
import { awardQuery, awardsQuery, certificateTemplatesQuery } from "../../../services/queries";
import { viewQuery } from "../scopedQuery";
import { toNewAward } from "../../certificates/awardHandover";
import { awardeeFullName } from "../../certificates/awards";
import { CERTIFICATE_STYLES } from "../../certificates/certificateTemplates";
import { significantWords } from "../significantWords";
import { dayLabel } from "../dateRange";
import { askedHow } from "./askedHow";
import { askFor } from "./findByName";
import { AnswerContext, defineEntry, HandoffEntry, isSlotQuestion } from "./types";
import { capitalize, countOf, isAre, listUpTo, quoted, wasWere } from "./wording";

/** The Organisation's own published certificate templates, as Templates shows them, beside the ready-made ones. */
const loadPublishedTemplates = async ({ user, token, queryClient }: AnswerContext) =>
  (await queryClient.fetchQuery(viewQuery(user, certificateTemplatesQuery(user?.schoolId, token))))
    .filter((template) => template.status === "published")
    .map((template) => template.name);

const READY_MADE = CERTIFICATE_STYLES.length;

/** Every Award, newest first, as Awards lists them. */
async function loadAwards({ user, token, queryClient }: AnswerContext) {
  const awards = await queryClient.fetchQuery(viewQuery(user, awardsQuery(user?.schoolId, token)));
  return [...awards].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

type SavedAward = Awaited<ReturnType<typeof loadAwards>>[number];

/** Words of an Award question that aren't part of an Award's name. */
const AWARD_QUESTION_WORDS = new Set([
  "who", "which", "what", "how", "many", "has", "have", "had", "did", "was", "were", "been", "got", "get",
  "received", "receive", "won", "win", "given", "give", "gave", "award", "awarded", "awardee", "certificate",
  "children", "child", "kid", "learner", "pupil", "student", "people", "sent", "send", "out", "gone", "yet",
  "all", "everyone", "our", "my", "we", "us", "you", "list", "name", "show", "tell", "check", "with", "that",
  "this", "those", "these", "so", "far", "already", "by", "from",
]);

/**
 * The Award a question names: the one whose title shares the largest part of
 * its words with the question. With no name, the only Award if there's one.
 */
function pickAward(awards: SavedAward[], said: string) {
  const titles = awards.map((a) => a.title);
  const asked = Array.from(significantWords(said)).filter((w) => !AWARD_QUESTION_WORDS.has(w));
  if (asked.length === 0) {
    return awards.length === 1 ? { award: awards[0] } : askFor("award", "missing", "Which Award do you mean?", titles);
  }
  const share = (award: SavedAward) => {
    const title = significantWords(award.title);
    return title.size > 0 ? asked.filter((w) => title.has(w)).length / title.size : 0;
  };
  const best = Math.max(...awards.map(share));
  if (best === 0) return askFor("award", "notFound", "I couldn't find that Award. Which one do you mean?", titles);
  const found = awards.filter((a) => share(a) === best);
  if (found.length > 1) return askFor("award", "ambiguous", "Which Award do you mean?", found.map((a) => a.title));
  return { award: found[0] };
}

/** The Award a question names and its Certificates, as its Awardees tab lists them. */
async function loadNamedAward(said: string, ctx: AnswerContext) {
  const awards = await loadAwards(ctx);
  if (awards.length === 0) return null;
  const pick = pickAward(awards, said);
  if (isSlotQuestion(pick)) return pick;
  const { user, token, queryClient } = ctx;
  const { certificates } = await queryClient.fetchQuery(
    viewQuery(user, awardQuery(user?.schoolId, token, pick.award.awardId))
  );
  return { awardId: pick.award.awardId, title: pick.award.title, certificates };
}

const NO_AWARDS = "You haven't made any Awards yet.";

const openAwards = () => ({ label: "Open Awards", href: AWARDS });
const openAward = (award: { awardId: string } | null) =>
  award ? { label: "Open the Award", href: awardHref(award.awardId) } : openAwards();

type SentState = { title: string; total: number; sent: number; notSent: string[] };

/** All sent, none sent and no Awardees read the same in every wording; only a part-sent Award varies. */
function sentText(award: SentState | null, partly: (award: SentState) => string): string {
  if (!award) return NO_AWARDS;
  const { title, total, sent } = award;
  if (total === 0) return `${quoted(title)} has no Awardees yet, so there's nothing to send.`;
  if (sent === total) {
    return total === 1
      ? `The certificate on ${quoted(title)} has been sent.`
      : `All ${total} certificates on ${quoted(title)} have been sent.`;
  }
  if (sent === 0) {
    return total === 1
      ? `The certificate on ${quoted(title)} hasn't been sent yet.`
      : `None of the ${total} certificates on ${quoted(title)} have been sent yet.`;
  }
  return partly(award);
}

/** Words of a "does Ayanda have a certificate?" question that aren't the person's name. */
const PERSON_QUESTION_WORDS = new Set([...Array.from(AWARD_QUESTION_WORDS), "any", "some", "their", "her", "his"]);

type PersonCertificates = { name: string | null; held: { award: string; sentAt?: string }[]; said?: string };

/** "2 certificates: “Swimming – Gold” (sent Saturday, October 3) and “Reading Star” (not sent yet)" */
function personCertificates(
  found: PersonCertificates,
  sentence: (name: string, list: string) => string
): string {
  if (!found.name) return `I couldn't find a certificate for ${found.said ?? "them"} on any Award.`;
  const items = found.held.map(({ award, sentAt }) => {
    const sent = sentAt ? new Date(sentAt) : null;
    const state = sent && !Number.isNaN(sent.getTime()) ? `sent ${dayLabel(sent)}` : "not sent yet";
    return `${quoted(award)} (${state})`;
  });
  return sentence(found.name, `${countOf(items.length, "certificate")}: ${listUpTo(items)}`);
}

/** "Make Gold certificates for the Maths Olympiad winners": New Award, with its name filled in. Nothing is saved until Save and add Awardees. */
const newAwardEntry: HandoffEntry = {
  id: "certificates.newAward",
  sidebarItem: "create",
  examples: [
    "Make certificates",
    "Create certificates",
    "Make an Award",
    "Create a new Award",
    "Start a new Award",
    "New Award",
    "Set up an Award",
    "I want to make certificates",
    "Create award certificates",
    "Make certificates for the winners",
    "What if I pick the wrong child?",
    "So the button is Save and add Awardees, not Publish?",
    "the button is Save and add Awardees, not Publish?",
    ...askedHow("create a certificate"),
    "I'll check the names before I save",
  ],
  handoff: async (said) => {
    const title = said.award?.trim();
    if (!title) return askFor("award", "missing", "What should the Award be called? Nothing is saved until you press Save and add Awardees.", []);
    return {
      kind: "ready",
      text: "Here's what I understood. Open it to check. Nothing is saved until you press Save and add Awardees.",
      summary: [{ label: "Award", value: title }],
      to: toNewAward({ title }),
    };
  },
};

/** Certificates, opened from Create: certificate templates, Awards and who received them. */
export const certificatesEntries = [
  // Published certificate templates, as Templates shows them: the ready-made ones and the Organisation's own
  defineEntry<{ own: string[] }>({
    id: "certificates.templates",
    sidebarItem: "create",
    examples: [
      "Which certificate templates are published?",
      "What certificate templates do we have?",
      "Which certificate templates can I use?",
      "How many certificate templates are there?",
      "List our certificate templates",
      "Show me the certificate designs",
      "What certificate designs are published?",
      "Which certificate templates can I use for an Award?",
      "Have we published any certificate templates?",
      "Do we have our own certificate template?",
      "Where are the certificate templates?",
      "Can I use the same award again next term?",
    ],
    answer: {
      load: async (_, ctx) => ({ own: await loadPublishedTemplates(ctx) }),
      templates: [
        ({ own }) =>
          own.length === 0
            ? `You have the ${READY_MADE} ready-made certificate templates. None of your own are published yet.`
            : `You have ${READY_MADE + own.length} published certificate templates: the ${READY_MADE} ready-made ones and ${listUpTo(own)}.`,
        ({ own }) =>
          own.length === 0
            ? `Only the ${READY_MADE} ready-made certificate templates are published. You haven't published one of your own yet.`
            : `${READY_MADE + own.length} certificate templates are published: ${listUpTo(own)}, plus the ${READY_MADE} ready-made ones.`,
        ({ own }) =>
          own.length === 0
            ? `The ${READY_MADE} ready-made certificate templates are ready to use. None of your own are published yet.`
            : `Besides the ${READY_MADE} ready-made certificate templates, you've published ${countOf(own.length, "of your own", "of your own")}: ${listUpTo(own)}.`,
      ],
      link: () => ({ label: "Open Templates", href: CERTIFICATE_TEMPLATES }),
    },
  }),

  // Awards, newest first, as Awards lists them
  defineEntry<{ titles: string[] }>({
    id: "certificates.awards",
    sidebarItem: "create",
    examples: [
      "What Awards have we made?",
      "What Awards do we have?",
      "List our Awards",
      "Show me the Awards",
      "How many Awards are there?",
      "Which Awards have we given?",
      "What certificates have we made?",
      "Which certificates have we given out?",
      "Our latest Awards",
      "Have we made any Awards?",
      "Which awards have we already made?",
    ],
    answer: {
      load: async (_, ctx) => ({ titles: (await loadAwards(ctx)).map((a) => quoted(a.title)) }),
      templates: [
        ({ titles }) =>
          titles.length === 0 ? NO_AWARDS : `You have ${countOf(titles.length, "Award")}: ${listUpTo(titles)}.`,
        ({ titles }) =>
          titles.length === 0
            ? NO_AWARDS
            : `There ${isAre(titles.length)} ${countOf(titles.length, "Award")}, newest first: ${listUpTo(titles)}.`,
        ({ titles }) =>
          titles.length === 0 ? NO_AWARDS : `${capitalize(countOf(titles.length, "Award"))} so far: ${listUpTo(titles)}.`,
      ],
      link: () => openAwards(),
    },
  }),

  // Who received an Award: its Awardees
  defineEntry<{ award: { awardId: string; title: string; names: string[] } | null }>({
    id: "certificates.awardees",
    sidebarItem: "create",
    examples: [
      "Who received the award?",
      "Who got the award?",
      "Which children received the award?",
      "Which children got a certificate?",
      "Who won the award?",
      "Who are the Awardees?",
      "List the Awardees",
      "Who is getting a certificate?",
      "Which learners got the award?",
      "How many children received the award?",
      "Who was given the award?",
      ...askedHow("see who received an award"),
    ],
    slots: { award: "" },
    answer: {
      load: async ({ award: said = "" }, ctx) => {
        try {
          const found = await loadNamedAward(said, ctx);
          if (isSlotQuestion(found)) return found;
          return {
            award: found && { ...found, names: found.certificates.map(awardeeFullName) },
          };
        } catch {
          return { award: null };
        }
      },
      templates: [
        ({ award }) =>
          !award
            ? NO_AWARDS
            : award.names.length === 0
              ? `${quoted(award.title)} has no Awardees yet.`
              : `${capitalize(countOf(award.names.length, "Awardee"))} received ${quoted(award.title)}: ${listUpTo(award.names)}.`,
        ({ award }) =>
          !award
            ? NO_AWARDS
            : award.names.length === 0
              ? `No one has been added to ${quoted(award.title)} yet.`
              : `${quoted(award.title)} went to ${listUpTo(award.names)}.`,
        ({ award }) =>
          !award
            ? NO_AWARDS
            : award.names.length === 0
              ? `There are no Awardees on ${quoted(award.title)} yet.`
              : `The Awardees for ${quoted(award.title)} are ${listUpTo(award.names)}.`,
      ],
      link: (_, { award }) => openAward(award),
    },
  }),

  // One person's certificates across every Award, as each Award's Awardees tab lists them
  defineEntry<PersonCertificates>({
    id: "certificates.forPerson",
    sidebarItem: "create",
    examples: [
      "Does Ayanda have any certificates?",
      "Does Thandi have a certificate?",
      "What certificates has Ayanda got?",
      "Which certificates does Sipho have?",
      "Has Lebo received an award?",
      "Has Thandi got any awards?",
      "What awards has Ayanda won?",
      "Show me Sipho's certificates",
      "Ayanda's certificates",
      "Did Lebo get a certificate?",
    ],
    slots: { child: "" },
    answer: {
      load: async ({ child: said = "" }, ctx) => {
        const asked = Array.from(significantWords(said)).filter((w) => !PERSON_QUESTION_WORDS.has(w));
        if (asked.length === 0) return askFor("child", "missing", "Whose certificates do you mean? Tell me their name.", []);
        const awards = await loadAwards(ctx);
        const { user, token, queryClient } = ctx;
        const withCertificates = await Promise.all(
          awards.map(async (award) => ({
            award,
            certificates: (await queryClient.fetchQuery(viewQuery(user, awardQuery(user?.schoolId, token, award.awardId))))
              .certificates,
          }))
        );
        const theirs = withCertificates.flatMap(({ award, certificates }) =>
          certificates
            .filter((c) => {
              const name = significantWords(awardeeFullName(c));
              return asked.every((w) => name.has(w));
            })
            .map((c) => ({ person: awardeeFullName(c), award: award.title, sentAt: c.sentAt }))
        );
        if (theirs.length === 0) return { name: null, held: [], said: capitalize(asked.join(" ")) };
        const people = Array.from(new Set(theirs.map((t) => t.person)));
        if (people.length > 1) return askFor("child", "ambiguous", "Which person do you mean?", people);
        return { name: people[0], held: theirs.map(({ award, sentAt }) => ({ award, sentAt })) };
      },
      templates: [
        (found) => personCertificates(found, (n, list) => `${n} has ${list}.`),
        (found) => personCertificates(found, (n, list) => `${n} is on ${list}.`),
        (found) => personCertificates(found, (n, list) => `I found ${list} for ${n}.`),
      ],
      link: () => openAwards(),
      subjectOf: (found) => found.name ?? undefined,
    },
  }),

  // Whether an Award's certificates have been sent, as its Awardees tab shows each one
  defineEntry<{ award: { awardId: string; title: string; total: number; sent: number; notSent: string[] } | null }>({
    id: "certificates.sent",
    sidebarItem: "create",
    examples: [
      "Have the certificates been sent?",
      "Show sent certificates",
      "Which certificates have we sent?",
      "Have the award certificates gone out?",
      "Were the certificates sent?",
      "Did the certificates go out?",
      "Which certificates haven't been sent?",
      "Who hasn't got their certificate yet?",
      "How many certificates have been sent?",
      "Are the certificates sent yet?",
      "Have parents received the certificates?",
      "Is the award sent?",
      "Have the merit certificates been sent?",
      "Have any certificates been sent?",
      "How many certificates have we issued?",
      "How many certificates did we give out?",
    ],
    slots: { award: "" },
    answer: {
      load: async ({ award: said = "" }, ctx) => {
        const found = await loadNamedAward(said, ctx);
        if (isSlotQuestion(found)) return found;
        if (!found) return { award: null };
        const { certificates, ...award } = found;
        const notSent = certificates.filter((c) => !c.sentAt).map(awardeeFullName);
        return { award: { ...award, total: certificates.length, sent: certificates.length - notSent.length, notSent } };
      },
      templates: [
        ({ award }) => sentText(award, (a) => `${a.sent} of ${countOf(a.total, "certificate")} on ${quoted(a.title)} ${wasWere(a.sent)} sent. Not sent yet: ${listUpTo(a.notSent)}.`),
        ({ award }) => sentText(award, (a) => `${quoted(a.title)} has ${a.sent} of ${a.total} certificates sent. Still to send: ${listUpTo(a.notSent)}.`),
        ({ award }) => sentText(award, (a) => `So far ${a.sent} of ${a.total} certificates on ${quoted(a.title)} have gone out. Not sent yet: ${listUpTo(a.notSent)}.`),
      ],
      link: (_, { award }) => openAward(award),
    },
  }),

  newAwardEntry,
];
